import React from "react";
import { Box, IconButton, Tooltip } from "@chakra-ui/react";

export const FloatingCardActionButton = React.forwardRef(({ label, icon, ...buttonProps }, ref) => (
  <IconButton
    ref={ref}
    aria-label={label}
    icon={
      <Box as="span" className="material-icons-outlined" fontSize="18px">
        {icon}
      </Box>
    }
    size="sm"
    w="38px"
    h="38px"
    minW="38px"
    borderRadius="full"
    bg="white"
    color="brand.700"
    border="1px solid"
    borderColor="gray.200"
    boxShadow="0 4px 14px rgba(26, 32, 44, 0.14)"
    transition="transform 150ms ease, box-shadow 150ms ease, background 150ms ease"
    _hover={{
      bg: "brand.800",
      color: "white",
      borderColor: "brand.800",
      boxShadow: "0 6px 18px rgba(26, 32, 44, 0.22)",
      transform: "translateY(-1px)",
    }}
    _active={{ transform: "translateY(0)", boxShadow: "0 2px 8px rgba(26, 32, 44, 0.18)" }}
    _focusVisible={{ boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.45)" }}
    {...buttonProps}
  />
));

FloatingCardActionButton.displayName = "FloatingCardActionButton";

/**
 * A non-portal tooltip for floating rails. Keeping it local avoids positioning
 * conflicts when the owning card is itself wrapped in another tooltip.
 */
export const FloatingCardActionTooltip = ({ label, children, isDisabled = false }) => (
  <Box
    position="relative"
    display="inline-flex"
    role="group"
    sx={isDisabled ? undefined : {
      "&:hover [data-floating-action-tooltip], &:focus-within [data-floating-action-tooltip]": {
        opacity: 1,
        visibility: "visible",
        transform: "translate(0, -50%)",
      },
    }}
  >
    {children}
    <Box
      data-floating-action-tooltip
      role="tooltip"
      position="absolute"
      zIndex={10}
      left="calc(100% + 8px)"
      top="50%"
      w="max-content"
      maxW="260px"
      px={2}
      py={1}
      borderRadius="md"
      bg="gray.900"
      color="white"
      fontSize="xs"
      lineHeight="short"
      boxShadow="md"
      opacity={0}
      visibility="hidden"
      pointerEvents="none"
      transform="translate(-4px, -50%)"
      transition="opacity 140ms ease, transform 140ms ease, visibility 140ms ease"
      whiteSpace="normal"
    >
      {label}
    </Box>
  </Box>
);

/**
 * A reusable action rail for controls that belong to a card without living
 * inside its visual surface. The containing card wrapper owns reveal-on-hover
 * behaviour so the timestamp and rail can animate together.
 */
const FloatingCardActions = ({ actions = [], children, top = 1 }) => (
  <Box
    data-floating-card-actions
    position="absolute"
    zIndex={2}
    top={{ base: "-18px", md: top }}
    right={{ base: 3, md: "-48px" }}
    display="flex"
    flexDirection={{ base: "row", md: "column" }}
    gap={2}
    opacity={0}
    transform={{ base: "translateY(6px)", md: "translateX(-6px)" }}
    pointerEvents="none"
    transition="opacity 180ms ease, transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)"
    sx={{
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    }}
  >
    {children || actions.map(({ label, icon, onClick, isDisabled = false }) => (
      <Tooltip key={label} label={label} placement="right" openDelay={350}>
        <FloatingCardActionButton
          label={label}
          icon={icon}
          onClick={onClick}
          isDisabled={isDisabled}
        />
      </Tooltip>
    ))}
  </Box>
);

export default FloatingCardActions;
